import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { submitCampaignInterest } from '../services/campaignService';
import CampaignCard from '../components/ui/CampaignCard';
import TakeoverCampaignCard from '../components/ui/TakeoverCampaignCard';
import useMediaQuery from '../hooks/useMediaQuery';

export default function CampaignsPage() {
  const { getCachedCampaigns, getCachedAvailableCampaigns, getCachedTakeoverCampaigns, getStale, invalidate } = useData();
  const stale = getStale('campaigns');
  const staleAvailable = getStale('availableCampaigns');
  const staleTakeover = getStale('takeoverCampaigns');
  const [active, setActive] = useState(stale?.active || []);
  const [completed, setCompleted] = useState(stale?.completed || []);
  const [available, setAvailable] = useState(staleAvailable?.campaigns || []);
  const [loading, setLoading] = useState(!stale);
  const [error, setError] = useState(null);
  const [takeoverCampaigns, setTakeoverCampaigns] = useState(staleTakeover || []);
  const [interestLoading, setInterestLoading] = useState({});
  const [expandedAvailableId, setExpandedAvailableId] = useState(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const [data, availableData] = await Promise.all([
          getCachedCampaigns(),
          getCachedAvailableCampaigns()
        ]);
        if (!mounted) return;
        setActive(data?.active || []);
        setCompleted(data?.completed || []);
        setAvailable(availableData?.campaigns || []);
      } catch {
        if (mounted) setError('Failed to load campaigns.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [getCachedCampaigns, getCachedAvailableCampaigns]);

  const handleInterest = async (campaignId) => {
    setInterestLoading(prev => ({ ...prev, [campaignId]: true }));
    try {
      await submitCampaignInterest(campaignId);
      invalidate('availableCampaigns');
      invalidate('campaigns');
      invalidate('stats');
      invalidate('activity');
      const refreshed = await getCachedAvailableCampaigns(true);
      setAvailable(refreshed?.campaigns || []);
    } catch (err) {
      console.error('[CampaignsPage] interest submit error:', err);
      setError(err?.response?.data?.error || 'Failed to submit interest.');
    } finally {
      setInterestLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  useEffect(() => {
    let mounted = true;
    getCachedTakeoverCampaigns()
      .then(data => {
        if (mounted) {
          setTakeoverCampaigns(data || []);
        }
      })
      .catch(err => { console.error('[CampaignsPage] takeover fetch error:', err); });
    return () => { mounted = false; };
  }, [getCachedTakeoverCampaigns]);

  if (loading && active.length === 0) return <div className="loading-state">Loading...</div>;
  if (error && active.length === 0 && completed.length === 0) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="sec-eyebrow">Your Assignments</div>
      <div className="sec-title">Campaigns</div>
      <div className="sec-sub">Open campaigns, active assignments, and past work.</div>

      <div className="sec-head">Timeline Takeover</div>
      <div className="sec-sub" style={{ marginBottom: isMobile ? 24 : 16 }}>Open and recently closed campaigns — compete, earn, and climb the leaderboard.</div>
      {takeoverCampaigns.length === 0 ? (
        <div style={{ color: '#555', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', padding: '24px 0' }}>
          No takeover campaigns available.
        </div>
      ) : (
        <div className="camp-grid">
          {takeoverCampaigns.map((c) => (
            <TakeoverCampaignCard
              key={c.id || c._id}
              campaign={c}
              onClick={() => navigate(`/takeover/${c.id || c._id}`)}
            />
          ))}
        </div>
      )}

      <div className="sec-head">Recruiting Creators</div>
      <div className="sec-sub" style={{ marginBottom: 16 }}>Campaigns currently looking for creators. Show interest to be considered; you are not assigned until the team selects you.</div>
      {available.length === 0 ? (
        <div style={{ color: '#555', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', padding: '24px 0' }}>
          No recruiting campaigns open right now.
        </div>
      ) : (
        <div className="camp-grid">
          {available.map((c) => {
            const campaignId = c.id || c._id;
            const isExpanded = expandedAvailableId === campaignId;
            return (
              <React.Fragment key={campaignId}>
                <CampaignCard
                  campaign={{
                    ...c,
                    deliveryStatus: c.hasInterest ? 'SHOWED_ACTIVITY' : 'TO_DO',
                    notice: c.hasInterest
                      ? 'Interest received. You are not assigned yet; the team will move it to Active if you are selected.'
                      : 'Recruiting now. Show interest to be considered; this is not an active assignment yet.'
                  }}
                  actionLabel={isExpanded ? 'Hide Info' : 'More Info'}
                  actionDisabled={!!interestLoading[campaignId]}
                  onAction={() => {
                    setExpandedAvailableId(prev => prev === campaignId ? null : campaignId);
                  }}
                />
                {isExpanded && (
                  <div className="available-detail">
                    <div className="available-detail-main">
                      <div className="available-detail-label">Recruiting Brief</div>
                      <div className="available-detail-title">{c.name}</div>
                      <div className="available-detail-copy">
                        {c.briefSummary || 'Review the campaign, decide if it fits your content, then show interest. The team will select creators from this pool.'}
                      </div>
                      {c.briefUrl && (
                        <a className="available-detail-link" href={c.briefUrl} target="_blank" rel="noopener noreferrer">
                          Open Brief
                        </a>
                      )}
                    </div>
                    {c.requirements?.length > 0 && (
                      <div className="available-detail-list">
                        <div className="available-detail-label">Creator Fit</div>
                        {c.requirements.map((req, i) => (
                          <div className="available-detail-req" key={i}>
                            <span>{String(i + 1).padStart(2, '0')}</span>
                            <p>{req}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="available-detail-actions">
                      {c.hasInterest ? (
                        <button className="camp-action" disabled>
                          Interest Sent
                        </button>
                      ) : (
                        <button
                          className="camp-action"
                          disabled={!!interestLoading[campaignId]}
                          onClick={() => handleInterest(campaignId)}
                        >
                          {interestLoading[campaignId] ? 'Sending...' : 'Show Interest'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="sec-head">Active</div>
      {active.length === 0 ? (
        <div style={{ color: '#555', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', padding: '24px 0' }}>
          No active campaigns.
        </div>
      ) : (
        <div className="camp-grid">
          {active.map((c) => (
            <CampaignCard
              key={c.id || c._id}
              campaign={c}
              onClick={() => navigate(`/campaigns/${c.id || c._id}`)}
            />
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <>
          <div className="sec-head">Completed</div>
          <div className="camp-grid">
            {completed.map((c) => (
              <CampaignCard
                key={c.id || c._id}
                campaign={c}
                completed
                onClick={() => navigate(`/campaigns/${c.id || c._id}`)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}
